import { Folder } from "@mg/schemas/src/commons/folder";
import { Flex } from "@radix-ui/themes";
import { useLoaderData, useSearch } from "@tanstack/react-router";
import cx from "classnames";
import { usePostHog } from "posthog-js/react";
import { useEffect } from "react";

import { WorkflowReviewCard } from "./WorkflowReviewCard";

import { useDragSelect } from "../../../hooks/useDragSelect";
import { workflowsRoute } from "../route";

type Workflow = Pick<Folder, "_id" | "name" | "updatedAt" | "totalFiles"> & {
  thumbnails?: string[]; // The Folder type uses .default([]) which isn't picked up as optional here
};

// Helper function to categorize workflows by date
const categorizeWorkflows = (workflows: Workflow[]) => {
  const now = new Date();
  const oneWeekAgo = new Date(now);
  oneWeekAgo.setDate(now.getDate() - 7);
  oneWeekAgo.setHours(0, 0, 0, 0);

  const oneMonthAgo = new Date(now);
  oneMonthAgo.setMonth(now.getMonth() - 1);
  oneMonthAgo.setHours(0, 0, 0, 0);

  const thisWeek = workflows.filter((w) => {
    const date = w.updatedAt;
    return date && date >= oneWeekAgo;
  });

  const thisMonth = workflows.filter((w) => {
    const date = w.updatedAt;
    return date && date >= oneMonthAgo && date < oneWeekAgo;
  });

  // Group older workflows by month
  const olderWorkflows = workflows.filter((w) => {
    const date = w.updatedAt;
    return date && date < oneMonthAgo;
  });

  const monthGroups: Record<string, Workflow[]> = {};

  olderWorkflows.forEach((workflow) => {
    const date = workflow.updatedAt ? new Date(workflow.updatedAt) : new Date();
    const monthYear = `${date.toLocaleString("default", { month: "long" })} ${date.getFullYear()}`;

    if (!monthGroups[monthYear]) {
      monthGroups[monthYear] = [];
    }

    monthGroups[monthYear].push(workflow);
  });

  return {
    thisWeek,
    thisMonth,
    olderByMonth: Object.entries(monthGroups).map(([label, items]) => ({
      label,
      items,
    })),
  };
};

export function WorkflowGrid(props: {
  handleOpenFolder: (folderId: string) => void;
  onDeleteFolder: (folderId: string) => void;
  onRenameFolder: (folderId: string) => void;
  onShareFolder: (folderId: string) => void;
  onDownloadFolder: (folderId: string) => void;
}) {
  const { recentWorkflows } = useLoaderData({ from: workflowsRoute.id });
  const posthog = usePostHog();
  const { folderId } = useSearch({
    from: workflowsRoute.id,
  });

  const {
    containerRef,
    state: { selectedIds, isDraggingCards, selectionRect },
    handlers: {
      handleMouseDown,
      handleMouseMove,
      handleMouseUp,
      handleSelect,
      handleDragStart,
      handleDragEnter,
      handleDragLeave,
      handleDragOver,
      handleDrop,
    },
  } = useDragSelect<HTMLDivElement>();

  // Listen for the custom event from AppDrawer to reset selection state
  useEffect(() => {
    const resetDragState = (e: Event) => {
      handleDrop("", e);
    };

    // Listen for the custom event
    document.addEventListener("workflowsMovedToFolder", resetDragState);

    return () => {
      document.removeEventListener("workflowsMovedToFolder", resetDragState);
    };
  }, [handleDrop]);

  const renderWorkflowSections = () => {
    if (!folderId) {
      // Categorize workflows by date
      const categorized = categorizeWorkflows(recentWorkflows || []);
      return (
        <>
          {renderWorkflowSection(categorized.thisWeek, "Previous 7 days")}
          {renderWorkflowSection(categorized.thisMonth, "Previous 30 days")}

          {categorized.olderByMonth.map((group) =>
            renderWorkflowSection(group.items, group.label),
          )}
        </>
      );
    } else {
      // Inside of folders, just show all contents
      return renderWorkflowSection(recentWorkflows ?? []);
    }
  };

  const handleDoubleClick = (id: string) => {
    posthog.capture("double_clicked_workflow_card", {
      id,
      name: recentWorkflows?.find((w) => w._id === id)?.name,
    });
    props.handleOpenFolder(id);
  };

  // Render a section of workflow cards
  const renderWorkflowSection = (
    workflows: Workflow[],
    sectionTitle?: string,
  ) => {
    if (!workflows || workflows.length === 0) return null;

    return (
      <div className="mb-6" key={sectionTitle}>
        {sectionTitle && (
          <h2 className="mb-2 select-none text-sm font-medium text-puntt-neutral-grayA-11">
            {sectionTitle}
          </h2>
        )}
        <Flex gap="4" wrap="wrap">
          {workflows.map((t) => (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
            <div
              key={t._id}
              data-card-id={t._id}
              className={cx("relative border-2 transition-all duration-150", {
                "border-puntt-blue-9": selectedIds.includes(t._id!),
                "border-transparent": !selectedIds.includes(t._id!),
                "opacity-50": isDraggingCards && selectedIds.includes(t._id!),
              })}
              style={{
                borderRadius: "var(--radius-4)",
              }}
              draggable={true}
              onDragStart={(e) => handleDragStart(t._id!, e)}
              onDragEnter={(e) => handleDragEnter(t._id!, e)}
              onDragLeave={(e) => handleDragLeave(t._id!, e)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(t._id!, e)}
              onClick={(e) => handleSelect(t._id!, e)}
              onDoubleClick={() => handleDoubleClick(t._id!)}
            >
              <WorkflowReviewCard
                _id={t._id!}
                name={t.name!}
                updatedAt={t.updatedAt!}
                totalFiles={t.totalFiles ?? 0}
                thumbnails={t.thumbnails ?? []}
                handleOpenFolder={props.handleOpenFolder}
                onDeleteFolder={props.onDeleteFolder}
                onRenameFolder={props.onRenameFolder}
                onShareFolder={props.onShareFolder}
                onDownloadFolder={props.onDownloadFolder}
              />
            </div>
          ))}
        </Flex>
      </div>
    );
  };

  return (
    // eslint-disable-next-line jsx-a11y/interactive-supports-focus
    <div
      ref={containerRef}
      className="relative px-10 py-6"
      role="grid"
      id="workflow-grid"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      {renderWorkflowSections()}

      {/* Selection rectangle */}
      {selectionRect && (
        <div
          className="pointer-events-none absolute border-2 border-puntt-blue-9 bg-egyptian-blue-500/20"
          style={{
            left: Math.min(selectionRect.startX, selectionRect.currentX),
            top: Math.min(selectionRect.startY, selectionRect.currentY),
            width: Math.abs(selectionRect.currentX - selectionRect.startX),
            height: Math.abs(selectionRect.currentY - selectionRect.startY),
          }}
        />
      )}
    </div>
  );
}
